/**
 * Shared base styles for Letter Throwing Animation variants (web)
 *
 * Contains styles used by multiple animation variants.
 * Full Experience-specific styles are in fullExperienceStyles.web.ts.
 */

import { StyleSheet, Platform } from 'react-native';
import { CHROMATIC_COLORS } from './types';
import { fullExperienceStyles } from './fullExperienceStyles.web';

const baseStyles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    height: '100%',
    minHeight: 400,
  },
  gradient: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 600,
    width: '100%',
  },
  // Typewriter styles
  textContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
  },
  cursor: { color: '#ff6b9d', opacity: 0.8 },
  // Letter throwing styles
  flightZone: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pooledLetterWrapper: { position: 'absolute' },
  accumulatedContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    paddingHorizontal: 24,
  },
  // Shared badge/progress styles
  badgeContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryLabel: {
    fontSize: 14, fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 4, textTransform: 'uppercase', letterSpacing: 1,
  },
  progressContainer: {
    width: '80%', height: 4, backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 2, marginTop: 32, overflow: 'hidden',
  },
  progressBar: { height: '100%', backgroundColor: '#d4a5ff', borderRadius: 2 },
  phaseLabel: { marginTop: 12, fontSize: 14, color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' },
  skipHint: { position: 'absolute', bottom: 40, color: 'rgba(255, 255, 255, 0.5)', fontSize: 14 },
  // Narrator subtitle styles
  narratorSubtitleContainer: {
    position: 'absolute', bottom: 100, left: 32, right: 32, alignItems: 'center', zIndex: 50,
  },
  narratorSubtitle: {
    backgroundColor: 'rgba(10, 0, 18, 0.85)', borderRadius: 8,
    paddingHorizontal: 16, paddingVertical: 12, maxWidth: 400,
    // @ts-ignore -- web-only CSS property
    backdropFilter: 'blur(8px)',
  },
  // Word Convergence styles
  convergenceZone: {
    position: 'absolute', width: '100%', height: '100%',
    alignItems: 'center', justifyContent: 'center', overflow: 'visible',
  },
  convergingLetter: { position: 'absolute', alignItems: 'center', justifyContent: 'center' },
  chromaticLayerR: { position: 'absolute', fontSize: 18, fontWeight: '700', color: CHROMATIC_COLORS.r, opacity: 0.4 },
  chromaticLayerG: { position: 'absolute', fontSize: 18, fontWeight: '700', color: CHROMATIC_COLORS.g, opacity: 0.4 },
  chromaticLayerB: { position: 'absolute', fontSize: 18, fontWeight: '700', color: CHROMATIC_COLORS.b, opacity: 0.4 },
  formedTextContainer: { alignItems: 'center', justifyContent: 'center', minHeight: 120, paddingHorizontal: 24 },
});

// Web-only styles with CSS properties not in React Native's type system.
// Kept separate to avoid blanket `as any` on entire style objects.
// Using `as const` on string-literal props to preserve narrow types (e.g. fontWeight, textAlign, alignItems).
const text = {
  fontSize: 20, fontWeight: '600' as const, color: '#d4a5ff', textAlign: 'center' as const,
  lineHeight: 32, textShadow: '0px 0px 12px rgba(212, 165, 255, 0.6)', paddingHorizontal: 16,
};
const chromaticPink = {
  color: 'transparent', textShadow: '-2px 0px 4px rgba(255, 179, 230, 0.3)',
};
const chromaticMint = {
  color: 'transparent', textShadow: '2px 0px 4px rgba(165, 255, 235, 0.3)',
};
const accumulatedText = {
  fontSize: 20, fontWeight: '700' as const, color: '#FFFFFF', textAlign: 'center' as const,
  lineHeight: 32, textShadow: '0px 0px 8px rgba(212, 165, 255, 0.4)',
};
const badge = {
  width: 140, height: 140, borderRadius: 70,
  alignItems: 'center' as const, justifyContent: 'center' as const,
  boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.4)',
  borderWidth: 3, borderColor: 'rgba(255, 255, 255, 0.3)',
};
const scoreText = {
  fontSize: 48, fontWeight: '800' as const, color: '#FFFFFF',
  textShadow: '0px 2px 4px rgba(0, 0, 0, 0.3)',
};
const narratorSubtitleText = {
  fontFamily: Platform.OS === 'web'
    ? 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    : 'System',
  fontSize: 15, fontWeight: '500' as const, color: '#FFFFFF', textAlign: 'center' as const,
  letterSpacing: 0.2, textShadow: '0px 1px 3px rgba(0, 0, 0, 0.8)',
};
const mainLetter = {
  fontSize: 18, fontWeight: '700' as const, color: CHROMATIC_COLORS.main,
  textShadow: '0px 0px 8px rgba(212, 165, 255, 0.6)',
};
const formedText = {
  fontSize: 20, fontWeight: '700' as const, color: '#FFFFFF', textAlign: 'center' as const, lineHeight: 32,
  textShadow: '0px 0px 10px rgba(212, 165, 255, 0.6)',
};

const webStyles = { text, chromaticPink, chromaticMint, accumulatedText, badge, scoreText, narratorSubtitleText, mainLetter, formedText };

// Merge base + web-only + fullExperience styles into a single export for backward compatibility
export const styles = { ...baseStyles, ...webStyles, ...fullExperienceStyles };
